import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { T } from "@/components/T";
import { useLanguage } from "@/contexts/LanguageContext";

const leistungenLinks = {
  de: [
    { label: "Aufstockungen & Anbauten", href: "/leistungen/aufstockungen-anbauten" },
    { label: "Dachstühle & Holztragwerke", href: "/leistungen/dachstuehle-holztragwerke" },
    { label: "Holzhäuser", href: "/leistungen/holzhaeuser" },
    { label: "Hotelbau & Tourismusbau", href: "/leistungen/hotelbau-tourismusbau" },
    { label: "Holzfassaden", href: "/leistungen/holzfassaden" },
    { label: "Sanierungen & Restaurierungen", href: "/leistungen/sanierungen-restaurierungen" },
  ],
  it: [
    { label: "Sopraelevazioni & Ampliamenti", href: "/leistungen/aufstockungen-anbauten" },
    { label: "Capriate & Strutture in Legno", href: "/leistungen/dachstuehle-holztragwerke" },
    { label: "Case in Legno", href: "/leistungen/holzhaeuser" },
    { label: "Edilizia Alberghiera", href: "/leistungen/hotelbau-tourismusbau" },
    { label: "Facciate in Legno", href: "/leistungen/holzfassaden" },
    { label: "Risanamenti & Restauri", href: "/leistungen/sanierungen-restaurierungen" },
  ],
};

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { lang } = useLanguage();
  const links = leistungenLinks[lang];

  return (
    <footer className="bg-hb-dark text-white" data-testid="main-footer">
      <div className="border-b" style={{ borderColor: "rgba(245,200,0,0.15)", background: "rgba(245,200,0,0.05)" }}>
        <div className="container-hb py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-lg font-bebas text-white mb-1" style={{ fontSize: "28px", letterSpacing: "0.02em" }}>
              <T de="Projekt besprechen?" it="Discutere un progetto?" />
            </p>
            <p className="text-sm text-white/55 font-inter">
              <T de="Wir antworten in der Regel innerhalb von 24 Stunden." it="Di solito rispondiamo entro 24 ore." />
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/kontakt" data-testid="footer-cta-btn" className="btn-primary">
              <T de="Projekt anfragen" it="Richiedi un progetto" />
            </Link>
            <a href="tel:+390473238030" data-testid="footer-phone-btn"
              className="flex items-center justify-center gap-2 text-xs tracking-widest uppercase font-semibold px-6 py-4 transition-all"
              style={{ border: "1px solid rgba(245,200,0,0.25)", color: "rgba(255,255,255,0.6)" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(245,200,0,0.6)"; e.currentTarget.style.color = "#f5c800"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(245,200,0,0.25)"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}>
              <Phone size={12} /> +39 0473 238 030
            </a>
          </div>
        </div>
      </div>
      <div className="container-hb py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img src="/logo-white-yellow.png" alt="Holzbuddy.team" className="h-9 logo-glow" />
            </Link>
            <p className="text-sm text-white/60 font-inter leading-relaxed mb-6">
              <T de={<>Premium-Holzbau mit System.<br />Präzise geplant. Sauber umgesetzt.</>}
                 it={<>Costruzioni in legno con sistema.<br />Pianificato con precisione. Realizzato con cura.</>} />
            </p>
            <p className="text-xs text-white/40 font-inter">
              <T de="Eine Marke der" it="Un marchio di" /><br />
              <a href="https://www.hafner-ec.com" target="_blank" rel="noopener noreferrer" className="text-hb-yellow hover:text-hb-yellow/80 transition-colors">
                Hafner EC GmbH
              </a>
            </p>
          </div>

          <div>
            <h4 className="text-xs tracking-widest uppercase text-white/40 font-semibold mb-6 font-inter">
              <T de="Leistungen" it="Servizi" />
            </h4>
            <ul className="space-y-3">
              {links.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-white/70 hover:text-white transition-colors font-inter">
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/leistungen" className="text-sm text-hb-wood hover:text-hb-wood/80 transition-colors font-inter flex items-center gap-1">
                  <T de="Alle Leistungen" it="Tutti i servizi" /> <ArrowUpRight size={12} />
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs tracking-widest uppercase text-white/40 font-semibold mb-6 font-inter">
              <T de="Unternehmen" it="Azienda" />
            </h4>
            <ul className="space-y-3">
              {(lang === "it"
                ? [
                    { label: "Chi siamo", href: "/ueber-uns" },
                    { label: "Progetti", href: "/projekte" },
                    { label: "Sostenibilità", href: "/nachhaltigkeit" },
                    { label: "Rivista & Conoscenza", href: "/magazin" },
                    { label: "Lavoro", href: "/karriere" },
                    { label: "Contatti", href: "/kontakt" },
                  ]
                : [
                    { label: "Über uns", href: "/ueber-uns" },
                    { label: "Projekte", href: "/projekte" },
                    { label: "Nachhaltigkeit", href: "/nachhaltigkeit" },
                    { label: "Magazin & Wissen", href: "/magazin" },
                    { label: "Karriere", href: "/karriere" },
                    { label: "Kontakt", href: "/kontakt" },
                  ]
              ).map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-white/70 hover:text-white transition-colors font-inter">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Partner */}
          <div>
            <h4 className="text-xs tracking-widest uppercase text-white/40 font-semibold mb-6 font-inter">
              <T de="Partner" it="Partner" />
            </h4>
            <ul className="space-y-3">
              {[
                { label: "Hafner EC GmbH", href: "https://www.hafner-ec.com" },
                { label: "Amadeus Immobilien", href: "https://www.amadeus.immo" },
                { label: "Kohlbrenner Lodges", href: "https://www.kohlbrenner.it" },
              ].map(p => (
                <li key={p.href}>
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/70 hover:text-hb-yellow transition-colors font-inter flex items-center gap-1.5"
                  >
                    {p.label} <ArrowUpRight size={11} className="opacity-50" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Kontakt */}
          <div>
            <h4 className="text-xs tracking-widest uppercase text-white/40 font-semibold mb-6 font-inter">
              Kontakt
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={15} className="text-hb-wood mt-0.5 flex-shrink-0" />
                <div className="text-sm text-white/70 font-inter">
                  Sinich-Bach-Str. 60<br />
                  I-39012 Meran (BZ)<br />
                  <T de="Südtirol, Italien" it="Alto Adige, Italia" />
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={15} className="text-hb-wood flex-shrink-0" />
                <a href="tel:+390473238030" className="text-sm text-white/70 hover:text-white transition-colors font-inter">+39 0473 238 030</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={15} className="text-hb-wood flex-shrink-0" />
                <a href="mailto:info@holzbuddy.team" className="text-sm text-white/70 hover:text-white transition-colors font-inter">info@holzbuddy.team</a>
              </li>
            </ul>
            <div className="mt-8 space-y-2">
              <a href="tel:+390473238030" data-testid="footer-phone-link"
                className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors font-inter">
                <Phone size={13} className="text-hb-wood" /> +39 0473 238 030
              </a>
              <p className="text-xs text-white/30 font-inter pl-5">
                <T de="Mo–Fr 7:00–18:00 Uhr" it="Lun–Ven 7:00–18:00" />
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-hb py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/30 font-inter">
            © {currentYear} Holzbuddy – <T de="Eine Marke der" it="Un marchio di" /> Hafner EC GmbH · P.IVA 02706840218
          </p>
          <div className="flex items-center gap-6">
            <span className="text-xs text-white/30 font-inter"><T de="Mo–Fr 7:00–18:00" it="Lun–Ven 7:00–18:00" /></span>
            <Link to="/datenschutz" className="text-xs text-white/30 hover:text-white/60 transition-colors font-inter">
              <T de="Datenschutz" it="Privacy" />
            </Link>
            <Link to="/impressum" className="text-xs text-white/30 hover:text-white/60 transition-colors font-inter">
              Impressum
            </Link>
            <Link to="/spiel" className="text-xs hover:text-white/60 transition-colors font-inter" style={{ color: "rgba(245,200,0,0.25)" }}>
              🪵
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
